// src/components/Inventory/RemnantsTab.js
// Pestaña "Retales" (§6): lista con filtros (categoría/estado/ubicación,
// búsqueda por código/material), totales por categoría, y acciones por fila:
// Usar (updateRemnantStatus → ITC remnant.used) · Descargar DXF · Imprimir
// etiqueta. Para cartón muestra la dirección de flauta (badge §5b).
// Lectura directa de `remnants` (permitida); las ESCRITURAS van por callable.
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/firebase-config';
import { t } from '../../lib/i18n';
import { buildRectangleDxf, downloadDxf } from '../../lib/dxf';
import { remnantDxfFilename } from '../../lib/naming';
import { buildRemnantLabelPdf } from '../../lib/remnantLabel';
import { locationLabel } from '../../lib/locations';

const STATUS_STYLES = {
    available: 'bg-green-100 text-green-800',
    reserved: 'bg-yellow-100 text-yellow-800',
    used: 'bg-gray-200 text-gray-600',
};

const RemnantsTab = ({ db, cfg, canManageInventory, onNewRemnant, refreshKey = 0 }) => {
    const [remnants, setRemnants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState({ search: '', category: 'all', status: 'all', place: 'all' });
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const snap = await getDocs(query(collection(db, 'remnants'), orderBy('createdAt', 'desc')));
            setRemnants(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error('Error cargando retales:', e);
        } finally {
            setIsLoading(false);
        }
    }, [db]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const categories = useMemo(() => [...new Set(remnants.map((r) => r.category).filter(Boolean))].sort(), [remnants]);
    // Lugares para filtrar: paletas y ubicaciones fijas presentes en los retales.
    const places = useMemo(() => {
        const set = new Map();
        remnants.forEach((r) => {
            const key = JSON.stringify(r.location || {});
            if (r.location) set.set(key, locationLabel(r.location, cfg) || '—');
        });
        return [...set.entries()].map(([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label));
    }, [remnants, cfg]);

    const filtered = useMemo(() => remnants.filter((r) => {
        const s = filters.search.toLowerCase();
        const matName = r.materialSnapshot?.name || '';
        const matchesSearch = !s || (r.code || '').toLowerCase().includes(s) || matName.toLowerCase().includes(s);
        const matchesCat = filters.category === 'all' || r.category === filters.category;
        const matchesStatus = filters.status === 'all' || r.status === filters.status;
        const matchesPlace = filters.place === 'all' || JSON.stringify(r.location || {}) === filters.place;
        return matchesSearch && matchesCat && matchesStatus && matchesPlace;
    }), [remnants, filters]);

    // Totales por categoría (piezas disponibles) — §6 "totales por categoría".
    const totals = useMemo(() => {
        const acc = {};
        filtered.forEach((r) => {
            if (r.status !== 'used') acc[r.category] = (acc[r.category] || 0) + (Number(r.quantity) || 0);
        });
        return Object.entries(acc).sort();
    }, [filtered]);

    const markUsed = async (r) => {
        const jobId = window.prompt(t('remnant.useJobPrompt')) || null;
        setBusyId(r.id);
        try {
            const fn = httpsCallable(functions, 'updateRemnantStatus');
            await fn({ remnantId: r.id, status: 'used', jobId });
            await load();
        } catch (e) {
            console.error('updateRemnantStatus falló:', e);
            alert(e.message || t('remnant.error'));
        } finally {
            setBusyId(null);
        }
    };

    const dxf = (r) => downloadDxf(
        buildRectangleDxf({ widthIn: r.m1, heightIn: r.m2 }),
        remnantDxfFilename({ code: r.code, widthIn: r.m1, heightIn: r.m2 }),
    );

    const label = (r) => buildRemnantLabelPdf({ remnant: r, locationText: locationLabel(r.location, cfg) });

    const handleFilter = (e) => setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));

    return (
        <div className="flex flex-col h-full">
            <div className="flex-shrink-0 p-4 bg-gray-50 space-y-3">
                <div className="flex flex-col md:flex-row gap-2">
                    <input type="text" name="search" placeholder={t('remnant.search')} value={filters.search}
                        onChange={handleFilter} className="flex-1 p-2 border border-gray-300 rounded-md" />
                    <select name="category" value={filters.category} onChange={handleFilter} className="p-2 border border-gray-300 rounded-md">
                        <option value="all">{t('materials.allCategories')}</option>
                        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select name="status" value={filters.status} onChange={handleFilter} className="p-2 border border-gray-300 rounded-md">
                        <option value="all">{t('remnant.allStatuses')}</option>
                        {['available', 'reserved', 'used'].map((s) => <option key={s} value={s}>{t(`remnant.status.${s}`)}</option>)}
                    </select>
                    <select name="place" value={filters.place} onChange={handleFilter} className="p-2 border border-gray-300 rounded-md">
                        <option value="all">{t('remnant.allLocations')}</option>
                        {places.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                    {canManageInventory && (
                        <button onClick={onNewRemnant} className="px-4 py-2 font-bold text-white bg-green-600 rounded-lg hover:bg-green-700">
                            {t('materials.addRemnant')}
                        </button>
                    )}
                </div>
                {totals.length > 0 && (
                    <p className="text-xs text-gray-600">
                        {t('remnant.totals')}{' '}
                        {totals.map(([cat, qty]) => <span key={cat} className="inline-block mr-3 font-semibold">{cat}: {qty}</span>)}
                    </p>
                )}
            </div>

            <div className="flex-grow overflow-auto">
                {isLoading ? <div className="text-center p-10">{t('materials.loading')}</div> : filtered.length === 0 ? (
                    <div className="text-center p-10 text-gray-500">{t('remnant.empty')}</div>
                ) : (
                    <table className="w-full text-sm text-left text-gray-600">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0 z-10">
                            <tr>
                                <th className="px-4 py-3">{t('remnant.col.code')}</th>
                                <th className="px-4 py-3">{t('remnant.col.material')}</th>
                                <th className="px-4 py-3 text-center">{t('remnant.col.measures')}</th>
                                <th className="px-4 py-3 text-center">{t('remnant.col.qty')}</th>
                                <th className="px-4 py-3">{t('remnant.col.location')}</th>
                                <th className="px-4 py-3 text-center">{t('remnant.col.status')}</th>
                                <th className="px-4 py-3 text-center">{t('remnant.col.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filtered.map((r) => (
                                <tr key={r.id} className={`hover:bg-gray-50 ${r.status === 'used' ? 'opacity-60' : ''}`}>
                                    <td className="px-4 py-3 font-mono font-semibold text-gray-900">{r.code}</td>
                                    <td className="px-4 py-3">
                                        <div>{r.materialSnapshot?.name || '—'}</div>
                                        <div className="text-xs text-gray-500">{r.category}</div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className="font-semibold">{r.m1} × {r.m2}</span>
                                        {r.fluteDirection === 'm1' && (
                                            <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-800 rounded">
                                                {t('remnant.flute.badge')}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center font-bold">{r.quantity}</td>
                                    <td className="px-4 py-3">{locationLabel(r.location, cfg) || '—'}</td>
                                    <td className="px-4 py-3 text-center">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${STATUS_STYLES[r.status] || 'bg-gray-100'}`}>
                                            {t(`remnant.status.${r.status}`) || r.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-center items-center gap-2 flex-wrap">
                                            <button onClick={() => dxf(r)} title={t('remnant.downloadDxf')}
                                                className="px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100">DXF</button>
                                            <button onClick={() => label(r)} title={t('remnant.printLabel')}
                                                className="px-2 py-1 text-xs font-semibold text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200">🏷</button>
                                            {canManageInventory && r.status === 'available' && (
                                                <button onClick={() => markUsed(r)} disabled={busyId === r.id}
                                                    className="px-2 py-1 text-xs font-semibold text-white bg-gray-700 rounded hover:bg-gray-900 disabled:opacity-40">
                                                    {t('remnant.use')}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default RemnantsTab;
